import type { EditorProps } from '@monaco-editor/react'
import { FormControl, FormHelperText, Typography } from '@mui/material'
import { CopilotCELPrompter } from 'components/core/copilot-cel'
import { DynamicEditor } from 'components/core/editor/editor'
import { SafeMarkdown } from 'components/core/markdown'
import { EditorValidationEnv, FormFieldCELContext } from 'pbts/c1/api/editor/v1/editor'
import { useState } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

type CommonProps<T extends FieldValues> = {
    error?: boolean
    helperText?: string
    control?: Control<T>
    name?: Path<T>
    enableFetchValidation?: boolean
    minHeight?: string | undefined
    validationEnv?: EditorValidationEnv
    formFieldContext?: FormFieldCELContext
    showVariableBrowser?: boolean
} & EditorProps

type C1CELEditorProps<T extends FieldValues> = CommonProps<T> & {
    copilot?: boolean
}

export const C1CELEditor = <T extends FieldValues>(
    {
        control,
        name,
        error,
        helperText,
        onChange,
        enableFetchValidation = true,
        minHeight = undefined,
        copilot,
        validationEnv,
        formFieldContext,
        ...rest
    }: C1CELEditorProps<T>,
): React.ReactElement => {
    const [copilotIsActive, setCopilotIsActive] = useState(false)
    const [copilotExplainerText, setCopilotExplainerText] = useState('')

    if (!control || !name) {
        return <FormControl error={error} sx={{ width: '100%' }}>
            <DynamicEditor onChange={onChange} validationEnv={validationEnv} formFieldContext={formFieldContext} {...rest} />
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    }

    const options = {
        ...rest.options,
        readOnly: copilotIsActive,
    }

    return <Controller
        name={name}
        control={control}
        render={({ field }): React.ReactElement =>
            <FormControl error={error} sx={{ width: '100%' }}>
                {/* Copilot prompter panel - shown when activated via toolbar icon */}
                {copilot && validationEnv && copilotIsActive && <CopilotCELPrompter
                    validationEnv={validationEnv}
                    setExpression={field.onChange}
                    setExplainerText={setCopilotExplainerText}
                    currentExpression={field.value}
                    onClose={(): void => {
                        setCopilotIsActive(false)
                        setCopilotExplainerText('')
                    }}
                />}
                <DynamicEditor
                    {...rest}
                    options={options}
                    value={field.value}
                    onChange={(v, e): void => {
                        onChange && onChange(v, e)
                        field.onChange(v)
                    }}
                    enableFetchValidation={enableFetchValidation}
                    minHeight={minHeight}
                    validationEnv={validationEnv}
                    formFieldContext={formFieldContext}
                    copilotActive={copilotIsActive}
                    onCopilotToggle={copilot && validationEnv ? setCopilotIsActive : undefined}
                />
                {copilotExplainerText && <Typography component="div" mt={1}>
                    <SafeMarkdown>{copilotExplainerText}</SafeMarkdown>
                </Typography>}
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </FormControl>
        }
    />
}
